import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { useNavigate, useParams } from 'react-router-dom';

import { AccountFormModal } from '../components/accounts/AccountFormModal';

import { ContactFormModal } from '../components/contacts/ContactFormModal';

import { ProjectFormModal } from '../components/projects/ProjectFormModal';

import { TaskFormModal } from '../components/tasks/TaskFormModal';

import { TicketAccountPanel } from '../components/tickets/TicketAccountPanel';
import { TicketLinkAccountModal } from '../components/tickets/TicketLinkAccountModal';

import { TicketAttachmentsPanel } from '../components/tickets/TicketAttachmentsPanel';

import { TicketComposeModal } from '../components/tickets/TicketComposeModal';

import { TicketMergeModal } from '../components/tickets/TicketMergeModal';

import { TicketFolderSidebar } from '../components/tickets/TicketFolderSidebar';

import { TicketList } from '../components/tickets/TicketList';

import { TicketMessageTimeline } from '../components/tickets/TicketMessageTimeline';

import { TicketActivityBubble } from '../components/tickets/TicketActivityBubble';
import { TicketBubblePanel } from '../components/tickets/TicketBubblePanel';

import { TicketRibbon } from '../components/tickets/TicketRibbon';

import { TicketTagChips } from '../components/tickets/TicketTagChips';

import { PageHeader } from '../components/ui/PageHeader';

import { useTicketInboxRealtime } from '../hooks/useTicketInboxRealtime';

import { useAuth } from '../contexts/AuthContext';

import { useCrmData } from '../contexts/CrmDataContext';

import { usePortal, usePortalPath } from '../contexts/PortalContext';

import { clientById, idsMatch } from '../utils/crmHelpers';

import {

  addTicketNote,

  updateTicketNote,

  blockSender,

  bulkMoveTicketsToTrash,

  bulkUpdateTickets,

  checkIsTicketAgent,

  createTicketFolder,

  deleteTicketFolder,

  deleteTicketMessage,

  fetchTicket,

  fetchTicketAgents,

  fetchTicketAttachments,

  fetchTicketFolderUnreadCounts,

  ensureCustomFoldersUnderInbox,
  fetchTicketMailboxes,

  flattenTicketFolders,

  fetchTicketMessages,

  fetchTicketStatusDefinitions,

  fetchTicketTagDefinitions,

  fetchTicketTagIds,

  fetchTickets,

  findContactByEmail,

  logTicketActivity,

  permanentlyDeleteTickets,

  messageNeedsAttachmentRefresh,

  regularFileAttachments,

  sameTicketMessages,

  revokeTicketAttachmentUrls,

  refreshTicketAttachments,

  sendTicketReply,

  sendClosingAckIfEnabled,

  mergeTickets,

  sendTicketFollowUpReminder,

  ticketNeedsFollowUp,

  fetchTicketWorkflowSettings,

  resolvePortalMailboxId,

  setTicketTags,

  updateTicket,

  uploadTicketAttachment,

  insertTicketMessageAttachment,

  upsertTicketTagDefinition,

} from '../lib/tickets';

import type {

  Ticket,

  TicketAgent,

  TicketAttachment,

  TicketComposeMode,

  TicketFolder,

  TicketMessage,

  TicketPriority,

  TicketStatus,

  TicketStatusDefinition,

  TicketTagDefinition,

} from '../types/tickets';



const PAGE_SIZE = 40;

function collectFolderSubtreeIds(folders: TicketFolder[], rootId: string): Set<string> {
  const ids = new Set<string>([rootId]);
  let grew = true;
  while (grew) {
    grew = false;
    for (const folder of folders) {
      if (folder.parent_id && ids.has(folder.parent_id) && !ids.has(folder.id)) {
        ids.add(folder.id);
        grew = true;
      }
    }
  }
  return ids;
}

const FALLBACK_STATUSES: TicketStatusDefinition[] = [
  { id: 'open', slug: 'open', name: 'Open', color: '#16a34a', sort_order: 10, is_active: true, is_closed: false, is_system: true, created_at: '' },
  { id: 'processing', slug: 'processing', name: 'Processing', color: '#ca8a04', sort_order: 20, is_active: true, is_closed: false, is_system: true, created_at: '' },
  { id: 'on_hold', slug: 'on_hold', name: 'On hold', color: '#64748b', sort_order: 30, is_active: true, is_closed: false, is_system: true, created_at: '' },
  { id: 'resolved', slug: 'resolved', name: 'Resolved', color: '#2563eb', sort_order: 40, is_active: true, is_closed: true, is_system: true, created_at: '' },
  { id: 'closed', slug: 'closed', name: 'Closed', color: '#475569', sort_order: 50, is_active: true, is_closed: true, is_system: true, created_at: '' },
  { id: 'spam', slug: 'spam', name: 'Spam', color: '#9b1c1c', sort_order: 60, is_active: true, is_closed: true, is_system: true, created_at: '' },
];



export function TicketsPage() {

  const { id: routeTicketId } = useParams<{ id?: string }>();

  const navigate = useNavigate();

  const { kind: portalKind } = usePortal();
  const isItPortal = portalKind === 'it';
  const ticketsPath = usePortalPath('tickets');

  const { profile } = useAuth();

  const { data: crmData, refresh: refreshCrm } = useCrmData();

  const actorEmail = profile?.email ?? '';



  const [allowed, setAllowed] = useState<boolean | null>(null);

  const [folders, setFolders] = useState<TicketFolder[]>([]);

  const [ticketMailboxes, setTicketMailboxes] = useState<import('../types/tickets').TicketMailbox[]>([]);

  const [unreadByFolder, setUnreadByFolder] = useState<Record<string, number>>({});

  const [folderId, setFolderId] = useState<string | null>(null);

  const [search, setSearch] = useState('');

  const [debouncedSearch, setDebouncedSearch] = useState('');

  const [statusFilter, setStatusFilter] = useState<TicketStatus | 'all'>('all');

  const [tickets, setTickets] = useState<Ticket[]>([]);

  const [listPage, setListPage] = useState(1);

  const [listTotal, setListTotal] = useState(0);

  const [bulkSelectedIds, setBulkSelectedIds] = useState<Set<string>>(() => new Set());

  const [listLoading, setListLoading] = useState(true);

  const [listError, setListError] = useState<string | null>(null);



  const [selectedId, setSelectedId] = useState<string | null>(routeTicketId ?? null);

  const [ticket, setTicket] = useState<Ticket | null>(null);

  const [messages, setMessages] = useState<TicketMessage[]>([]);

  const [attachmentsByMessage, setAttachmentsByMessage] = useState<

    Record<string, TicketAttachment[]>

  >({});

  const [detailLoading, setDetailLoading] = useState(false);

  const [detailError, setDetailError] = useState<string | null>(null);

  const [sendBusy, setSendBusy] = useState(false);



  const [tagDefs, setTagDefs] = useState<TicketTagDefinition[]>([]);

  const [statusDefs, setStatusDefs] = useState<TicketStatusDefinition[]>(FALLBACK_STATUSES);

  const [ticketTagIds, setTicketTagIds] = useState<string[]>([]);

  const [ticketAgents, setTicketAgents] = useState<TicketAgent[]>([]);

  const [noteBusy, setNoteBusy] = useState(false);



  const [composeOpen, setComposeOpen] = useState(false);

  const [composeMode, setComposeMode] = useState<TicketComposeMode>('reply');



  const [accountModalOpen, setAccountModalOpen] = useState(false);

  const [linkAccountModalOpen, setLinkAccountModalOpen] = useState(false);

  const [contactModalOpen, setContactModalOpen] = useState(false);

  const [contactModalClientId, setContactModalClientId] = useState('');

  const [contactModalEmail, setContactModalEmail] = useState('');

  const [projectModalOpen, setProjectModalOpen] = useState(false);

  const [taskModalOpen, setTaskModalOpen] = useState(false);

  const [attachRefreshing, setAttachRefreshing] = useState(false);

  const attachmentRefreshKeysRef = useRef(new Set<string>());
  const activityBtnRef = useRef<HTMLButtonElement>(null);
  const detailsBtnRef = useRef<HTMLButtonElement>(null);
  const activityDesktopRef = useRef<HTMLButtonElement>(null);
  const detailsDesktopRef = useRef<HTMLButtonElement>(null);
  const detailRequestRef = useRef(0);
  const forceUnreadIdsRef = useRef(new Set<string>());
  /** Ignore realtime echo briefly after local writes so the UI stays steady. */
  const localEchoUntilRef = useRef(0);
  const bumpLocalEcho = useCallback(() => {
    localEchoUntilRef.current = Date.now() + 2800;
  }, []);

  const [attachMsg, setAttachMsg] = useState<string | null>(null);

  const [detailsOpen, setDetailsOpen] = useState(false);
  const [activityOpen, setActivityOpen] = useState(false);
  const [editNoteId, setEditNoteId] = useState<string | null>(null);
  const [detailTab, setDetailTab] = useState<'messages' | 'attachments'>('messages');
  const [mergeOpen, setMergeOpen] = useState(false);
  const [mergeBusy, setMergeBusy] = useState(false);
  const [followUpWorkdays, setFollowUpWorkdays] = useState(2);
  const [followUpBusy, setFollowUpBusy] = useState(false);

  const [foldersOpen, setFoldersOpen] = useState(false);
  const [narrowDetail, setNarrowDetail] = useState(false);

  useEffect(() => {
    const narrowMq = window.matchMedia('(max-width: 1400px)');
    const update = () => setNarrowDetail(narrowMq.matches);
    update();
    narrowMq.addEventListener('change', update);
    return () => narrowMq.removeEventListener('change', update);
  }, []);



  useEffect(() => {

    const t = window.setTimeout(() => setDebouncedSearch(search.trim()), 250);

    return () => window.clearTimeout(t);

  }, [search]);



  useEffect(() => {

    void (async () => {

      const ok = await checkIsTicketAgent();

      setAllowed(ok);

    })();

  }, []);



  const loadFolders = useCallback(async () => {

    const [folderRows, unread] = await Promise.all([

      ensureCustomFoldersUnderInbox(),

      fetchTicketFolderUnreadCounts(),

    ]);

    setFolders(folderRows);

    setUnreadByFolder(unread);

    setFolderId((prev) => {

      if (prev) return prev;

      const inbox = folderRows.find((f) => f.slug === 'inbox');

      return inbox?.id ?? folderRows[0]?.id ?? null;

    });

  }, []);



  const loadTags = useCallback(async (mailboxId?: string | null) => {

    try {

      const id = mailboxId || (await resolvePortalMailboxId());

      if (!id) {

        setTagDefs([]);

        return;

      }

      setTagDefs(await fetchTicketTagDefinitions(id));

    } catch {

      setTagDefs([]);

    }

  }, []);



  const loadStatuses = useCallback(async (mailboxId?: string | null) => {

    try {

      setStatusDefs(await fetchTicketStatusDefinitions(mailboxId ?? undefined));

    } catch {

      setStatusDefs(FALLBACK_STATUSES);

    }

  }, []);



  const loadAgents = useCallback(async () => {

    try {

      const agents = await fetchTicketAgents();

      setTicketAgents(agents.filter((a) => a.is_active));

    } catch {

      setTicketAgents([]);

    }

  }, []);



  const statusLabelMap = useMemo(

    () =>

      Object.fromEntries(

        statusDefs.map((s) => [s.slug, { name: s.name, color: s.color }]),

      ),

    [statusDefs],

  );



  const loadList = useCallback(async (opts?: { quiet?: boolean }) => {

    if (!folderId) return;

    if (!opts?.quiet) setListLoading(true);

    setListError(null);

    const scrollEl = opts?.quiet
      ? (document.querySelector('.tickets-list-pane .tickets-mid') as HTMLElement | null)
      : null;
    const savedScrollTop = scrollEl?.scrollTop ?? null;

    try {
      const sentFolder = folders.find((f) => f.id === folderId && f.slug === 'sent' && !f.mailbox_id);
      const trashId = folders.find((f) => f.slug === 'trash' && !f.mailbox_id)?.id;
      const spamId = folders.find((f) => f.slug === 'spam' && !f.mailbox_id)?.id;

      const result = await fetchTickets({

        folderId,

        sentInclusive: Boolean(sentFolder),

        excludeFolderIds: sentFolder
          ? [trashId, spamId].filter((id): id is string => Boolean(id))
          : undefined,

        status: statusFilter,

        search: debouncedSearch,

        page: listPage,

        pageSize: PAGE_SIZE,

      });

      setTickets((prev) => {
        // Keep sticky mark-unread and any in-flight optimistic read/unread while
        // a quiet refresh lands (avoids red-dot flash / jump).
        return result.rows.map((row) => {
          if (forceUnreadIdsRef.current.has(row.id)) {
            return row.is_unread ? row : { ...row, is_unread: true };
          }
          const prior = prev.find((p) => p.id === row.id);
          if (
            prior &&
            prior.is_unread !== row.is_unread &&
            Date.now() < localEchoUntilRef.current
          ) {
            return { ...row, is_unread: prior.is_unread };
          }
          return row;
        });
      });

      setListTotal(result.total);

      if (savedScrollTop != null && scrollEl) {
        requestAnimationFrame(() => {
          scrollEl.scrollTop = savedScrollTop;
        });
      }

    } catch (e) {

      setListError(e instanceof Error ? e.message : 'Failed to load tickets.');

      setTickets([]);

      setListTotal(0);

    } finally {

      if (!opts?.quiet) setListLoading(false);

    }

  }, [folderId, statusFilter, debouncedSearch, listPage, folders]);



  const listPageCount = Math.max(1, Math.ceil(listTotal / PAGE_SIZE));



  const loadFoldersRef = useRef(loadFolders);
  loadFoldersRef.current = loadFolders;
  const bumpLocalEchoRef = useRef(bumpLocalEcho);
  bumpLocalEchoRef.current = bumpLocalEcho;

  const loadDetail = useCallback(async (
    ticketId: string,
    opts?: { quiet?: boolean; markRead?: boolean },
  ) => {

    const quiet = Boolean(opts?.quiet);
    const requestId = ++detailRequestRef.current;

    if (!quiet) setDetailLoading(true);

    setDetailError(null);

    try {

      const [t, msgs] = await Promise.all([fetchTicket(ticketId), fetchTicketMessages(ticketId)]);

      if (requestId !== detailRequestRef.current) return;

      if (!t) {

        setTicket(null);

        setMessages([]);

        setAttachmentsByMessage({});

        setTicketTagIds([]);

        setDetailError('Ticket not found.');

        return;

      }



      let current = t;

      const shouldMarkRead =
        opts?.markRead !== false && !forceUnreadIdsRef.current.has(ticketId);

      // Mark read immediately after fetch (before auto-link writes) so realtime
      // echo is suppressed and the list dot clears without waiting on linking.
      if (current.is_unread && shouldMarkRead) {
        bumpLocalEchoRef.current();
        current = { ...current, is_unread: false };
        setTickets((prev) =>
          prev.map((row) => (row.id === current.id ? { ...row, is_unread: false } : row)),
        );
        void updateTicket(current.id, { is_unread: false })
          .then(() => {
            setUnreadByFolder((prev) => {
              const key = current.folder_id;
              if (!key) return prev;
              const n = Math.max(0, (prev[key] ?? 1) - 1);
              if (prev[key] === n) return prev;
              return { ...prev, [key]: n };
            });
          })
          .catch(() => {
            setTickets((prev) =>
              prev.map((row) => (row.id === ticketId ? { ...row, is_unread: true } : row)),
            );
            setTicket((prev) => (prev && prev.id === ticketId ? { ...prev, is_unread: true } : prev));
          });
      } else if (!quiet) {
        // Still suppress realtime from auto-link / activity writes while opening.
        bumpLocalEchoRef.current();
      }

      if (!current.client_id && current.requester_email) {

        const linked = await findContactByEmail(current.requester_email);

        if (linked?.client_id) {

          current = await updateTicket(current.id, {

            client_id: linked.client_id,

            contact_id: linked.contact_id,

          });
          if (shouldMarkRead) current = { ...current, is_unread: false };

          await logTicketActivity(current.id, 'auto_link_account', linked, actorEmail);

        }

      } else if (current.client_id && !current.contact_id && current.requester_email) {

        const linked = await findContactByEmail(current.requester_email);

        if (linked?.contact_id && String(linked.client_id ?? '') === String(current.client_id)) {

          current = await updateTicket(current.id, { contact_id: linked.contact_id });
          if (shouldMarkRead) current = { ...current, is_unread: false };

          await logTicketActivity(current.id, 'auto_link_contact', linked, actorEmail);

        }

      }



      if (requestId !== detailRequestRef.current) return;

      setTicket(current);

      let atts = await fetchTicketAttachments(msgs.map((m) => m.id));

      let map: Record<string, TicketAttachment[]> = {};

      for (const att of atts) {

        (map[att.message_id] ??= []).push(att);

      }

      // Show the thread immediately — don't wait on Graph attachment refresh.
      setMessages((prev) => (quiet && sameTicketMessages(prev, msgs) ? prev : msgs));
      setAttachmentsByMessage(map);
      if (!quiet) setDetailLoading(false);

      const refreshKey = `${ticketId}:${msgs.map((m) => m.id).join(',')}`;
      const shouldRefresh =
        messageNeedsAttachmentRefresh(msgs, map) &&
        !attachmentRefreshKeysRef.current.has(refreshKey);

      if (shouldRefresh) {

        attachmentRefreshKeysRef.current.add(refreshKey);

        setAttachRefreshing(true);

        setAttachMsg(null);

        try {

          const result = await refreshTicketAttachments(ticketId, true);

          atts = await fetchTicketAttachments(msgs.map((m) => m.id));

          map = {};

          for (const att of atts) {

            (map[att.message_id] ??= []).push(att);

          }

          const refreshedMsgs = await fetchTicketMessages(ticketId);

          if (requestId !== detailRequestRef.current) return;

          setMessages((prev) => (sameTicketMessages(prev, refreshedMsgs) ? prev : refreshedMsgs));
          setAttachmentsByMessage(map);

          const fileCount = refreshedMsgs.reduce(
            (n, m) =>
              n + regularFileAttachments(map[m.id] ?? [], m.body_html || '').length,
            0,
          );

          if (fileCount > 0) {

            setAttachMsg(`Loaded ${fileCount} attachment(s) from mailbox.`);

          } else if (result.errors?.length) {

            setAttachMsg(result.errors[0] ?? 'No attachments returned from Graph.');

          }

        } catch (e) {

          setAttachMsg(e instanceof Error ? e.message : 'Failed to load attachments.');

        } finally {

          setAttachRefreshing(false);

        }

      }



      try {

        setTicketTagIds(await fetchTicketTagIds(ticketId));

      } catch {

        setTicketTagIds([]);

      }

    } catch (e) {

      if (requestId !== detailRequestRef.current) return;

      if (!quiet) {
        setDetailError(e instanceof Error ? e.message : 'Failed to load ticket.');
      }

    } finally {

      if (requestId === detailRequestRef.current && !quiet) setDetailLoading(false);

    }

  }, [actorEmail]);



  useEffect(() => {

    if (!allowed) return;

    void loadFolders().catch((e) => setListError(e instanceof Error ? e.message : String(e)));

    void fetchTicketMailboxes()
      .then(setTicketMailboxes)
      .catch(() => setTicketMailboxes([]));

    void loadTags();

    void loadStatuses();

    void loadAgents();

    void resolvePortalMailboxId()
      .then((mailboxId) =>
        mailboxId
          ? fetchTicketWorkflowSettings(mailboxId)
          : Promise.resolve({ auto_reopen_on_inbound: true, follow_up_workdays: 2 }),
      )
      .then((wf) => setFollowUpWorkdays(wf.follow_up_workdays ?? 2))
      .catch(() => setFollowUpWorkdays(2));

  }, [allowed, loadFolders, loadTags, loadStatuses, loadAgents]);



  useEffect(() => {
    if (!allowed || !ticket?.mailbox_id) return;
    void loadTags(ticket.mailbox_id);
    void loadStatuses(ticket.mailbox_id);
    void fetchTicketWorkflowSettings(ticket.mailbox_id)
      .then((wf) => setFollowUpWorkdays(wf.follow_up_workdays ?? 2))
      .catch(() => undefined);
  }, [allowed, ticket?.mailbox_id, loadTags, loadStatuses]);



  useEffect(() => {

    setListPage(1);

    setBulkSelectedIds(new Set());

  }, [folderId, statusFilter, debouncedSearch]);



  useEffect(() => {

    if (!allowed || !folderId) return;

    void loadList();

  }, [allowed, folderId, loadList]);



  useTicketInboxRealtime({
    enabled: allowed === true,
    onChange: () => {
      // Local writes (read/unread, status, tags…) already patched state — skip list reload.
      if (Date.now() < localEchoUntilRef.current) return;
      void loadFolders();
      void loadList({ quiet: true });
      if (selectedId && !composeOpen) void loadDetail(selectedId, { quiet: true, markRead: false });
    },
  });

  useEffect(() => {

    if (routeTicketId) setSelectedId(routeTicketId);

  }, [routeTicketId]);



  useEffect(() => {

    if (!allowed || !selectedId) {

      setTicket(null);

      setMessages([]);

      setTicketTagIds([]);

      return;

    }

    void loadDetail(selectedId);

  }, [allowed, selectedId, loadDetail]);

  useEffect(() => {
    setDetailsOpen(false);
    setActivityOpen(false);
    setEditNoteId(null);
  }, [selectedId]);

  const ticketNotes = useMemo(
    () => messages.filter((m) => m.direction === 'note'),
    [messages],
  );

  const customerMessages = useMemo(
    () => messages.filter((m) => m.direction !== 'note'),
    [messages],
  );



  const linkedClient = useMemo(

    () => (ticket?.client_id ? clientById(crmData.clients, ticket.client_id) : null),

    [crmData.clients, ticket?.client_id],

  );

  const clientNameById = useMemo(() => {
    const map = new Map<string, string>();
    for (const client of crmData.clients) {
      map.set(String(client.id), client.company_name || client.code || 'Account');
    }
    return map;
  }, [crmData.clients]);

  const contactNameById = useMemo(() => {
    const map = new Map<string, string>();
    for (const contact of crmData.contacts) {
      const name = [contact.first_name, contact.last_name].filter(Boolean).join(' ').trim();
      map.set(String(contact.id), name || contact.email || 'Contact');
    }
    return map;
  }, [crmData.contacts]);

  const activeFolderName = useMemo(() => {
    const match = flattenTicketFolders(folders).find((f) => f.id === folderId);
    return match?.name ?? 'Folders';
  }, [folders, folderId]);

  const trashFolderId = useMemo(
    () => folders.find((f) => f.slug === 'trash' && !f.mailbox_id)?.id ?? null,
    [folders],
  );
  const viewingTrash = Boolean(folderId && trashFolderId && folderId === trashFolderId);



  const contactsForClient = useMemo(() => {
    if (!ticket?.client_id) return [];
    return crmData.contacts.filter((c) => idsMatch(c.client_id, ticket.client_id));
  }, [crmData.contacts, ticket?.client_id]);

  const composeContactOptions = useMemo(() => {
    const byEmail = new Map<
      string,
      {
        id: string;
        first_name: string;
        last_name: string;
        email: string;
        client_id?: string;
        job_title?: string;
        phone?: string;
      }
    >();
    const add = (
      emailRaw: string | null | undefined,
      first: string,
      last: string,
      id: string,
    ) => {
      const email = emailRaw?.trim();
      if (!email?.includes('@')) return;
      const key = email.toLowerCase();
      if (byEmail.has(key)) return;
      byEmail.set(key, {
        id,
        first_name: first || email,
        last_name: last,
        email,
        client_id: '',
        job_title: '',
        phone: '',
      });
    };
    for (const c of contactsForClient) {
      const email = c.email?.trim();
      if (!email?.includes('@')) continue;
      byEmail.set(email.toLowerCase(), {
        id: String(c.id),
        first_name: c.first_name || '',
        last_name: c.last_name || '',
        email,
        client_id: c.client_id != null ? String(c.client_id) : '',
        job_title: c.job_title || '',
        phone: c.phone || '',
      });
    }
    for (const agent of ticketAgents) {
      const parts = (agent.display_name || '').trim().split(/\s+/);
      add(agent.email, parts[0] || agent.display_name || '', parts.slice(1).join(' '), `agent:${agent.id || agent.email}`);
    }
    for (const user of crmData.users) {
      const parts = (user.display_name || user.username || '').trim().split(/\s+/);
      add(
        user.email,
        parts[0] || user.display_name || user.username || '',
        parts.slice(1).join(' '),
        `user:${user.id || user.email}`,
      );
    }
    return [...byEmail.values()] as typeof contactsForClient;
  }, [contactsForClient, ticketAgents, crmData.users]);


  const projectsForClient = useMemo(() => {

    if (!ticket?.client_id) return [];

    return crmData.projects.filter((p) => idsMatch(p.client_id, ticket.client_id));

  }, [crmData.projects, ticket?.client_id]);



  const tasksForClient = useMemo(() => {

    if (!ticket?.client_id) return [];

    return crmData.tasks.filter((t) => idsMatch(t.client_id, ticket.client_id));

  }, [crmData.tasks, ticket?.client_id]);



  function selectTicket(id: string) {

    const alreadyOpen = id === selectedId && !narrowDetail;
    const listRowUnread = tickets.some((row) => row.id === id && row.is_unread);
    const detailUnread = ticket?.id === id && ticket.is_unread;
    const stickyUnread = forceUnreadIdsRef.current.has(id);

    // Re-clicking the open ticket after Mark as unread: mark read without remounting detail.
    if (alreadyOpen) {
      if (!listRowUnread && !detailUnread && !stickyUnread) return;
      forceUnreadIdsRef.current.delete(id);
      bumpLocalEcho();
      setTickets((prev) =>
        prev.map((row) => (row.id === id ? { ...row, is_unread: false } : row)),
      );
      setTicket((prev) => (prev && prev.id === id ? { ...prev, is_unread: false } : prev));
      const folderKey = ticket?.folder_id ?? folderId;
      if (folderKey) {
        setUnreadByFolder((prev) => {
          const n = Math.max(0, (prev[folderKey] ?? 1) - 1);
          if (prev[folderKey] === n) return prev;
          return { ...prev, [folderKey]: n };
        });
      }
      void updateTicket(id, { is_unread: false }).catch(() => {
        forceUnreadIdsRef.current.add(id);
        setTickets((prev) =>
          prev.map((row) => (row.id === id ? { ...row, is_unread: true } : row)),
        );
        setTicket((prev) => (prev && prev.id === id ? { ...prev, is_unread: true } : prev));
      });
      return;
    }

    // Suppress realtime list reload from auto-link / mark-read writes.
    bumpLocalEcho();
    // Opening a ticket always allows auto-read (clears sticky "keep unread" from ribbon).
    forceUnreadIdsRef.current.delete(id);
    revokeTicketAttachmentUrls();
    setSelectedId(id);
    setDetailTab('messages');
    setTicket(null);
    setMessages([]);
    setAttachmentsByMessage({});
    setDetailError(null);
    setAttachMsg(null);
    setDetailsOpen(false);
    setActivityOpen(false);
    setEditNoteId(null);
    // Optimistic: clear unread dot immediately when opening.
    setTickets((prev) =>
      prev.map((row) => (row.id === id && row.is_unread ? { ...row, is_unread: false } : row)),
    );

    navigate(`${ticketsPath}/${id}`);

  }



  function clearSelectedTicket() {

    setSelectedId(null);

    setTicket(null);
    setDetailsOpen(false);

    navigate(ticketsPath);

  }



  function selectFolder(id: string) {

    setFolderId(id);

    setSelectedId(null);

    setTicket(null);

    setFoldersOpen(false);

    navigate(ticketsPath);

  }



  function openCompose(mode: TicketComposeMode) {

    setComposeMode(mode);

    setComposeOpen(true);

  }



  async function patchTicket(

    patch: Partial<{

      status: TicketStatus;

      priority: TicketPriority;

      folder_id: string;

      client_id: string | null;

      contact_id: string | null;

      assignee_email: string;

    }>,

    action: string,

  ) {

    if (!ticket) return;

    bumpLocalEcho();
    const updated = await updateTicket(ticket.id, patch);

    setTicket(updated);
    setTickets((prev) => prev.map((row) => (row.id === updated.id ? { ...row, ...updated } : row)));

    await logTicketActivity(ticket.id, action, patch, actorEmail);

    if (patch.status === 'closed' && ticket.status !== 'closed') {
      const shouldSend = window.confirm(
        'Send closed-ticket email to customer now?\n\nSelect OK to send, Cancel to close without sending.',
      );
      if (shouldSend) {
        void sendClosingAckIfEnabled(updated).catch(() => {
          /* template off or send failed — ticket is still closed */
        });
      }
    }

    // Only reload the list when the active status filter may remove this row.
    if (
      statusFilter !== 'all' &&
      patch.status &&
      patch.status !== statusFilter
    ) {
      void loadList({ quiet: true });
    }

    void loadFolders();

  }



  async function handleLinkAccountSave(clientId: string, contactId: string) {
    if (!ticket) return;

    const nextClientId = clientId ? String(clientId) : null;
    let nextContactId = contactId ? String(contactId) : null;

    if (nextContactId) {
      const linked = crmData.contacts.find((c) => idsMatch(c.id, nextContactId));
      // Drop contact only when we know it belongs to a different account.
      if (linked && nextClientId && !idsMatch(linked.client_id, nextClientId)) {
        nextContactId = null;
      }
    }

    await patchTicket(
      { client_id: nextClientId, contact_id: nextContactId },
      'link_account',
    );
  }



  async function handleCreateFolder(name: string, parentId?: string | null) {

    await createTicketFolder(name, parentId);

    await loadFolders();

  }



  async function handleDeleteFolder(targetFolderId: string) {

    const gone = collectFolderSubtreeIds(folders, targetFolderId);

    const result = await deleteTicketFolder(targetFolderId);

    setFolderId((prev) => (prev && gone.has(prev) ? result.inboxId : prev));

    if (ticket && gone.has(ticket.folder_id)) {

      const updated = await fetchTicket(ticket.id);

      if (updated) setTicket(updated);

    }

    await loadFolders();

    await loadList();

  }



  async function handleToggleRead() {
    const ids = resolveActionTicketIds();
    if (ids.length === 0) return;

    const selectedRows =
      bulkSelectedIds.size > 0 ? tickets.filter((t) => bulkSelectedIds.has(t.id)) : ticket ? [ticket] : [];
    const markAsRead =
      selectedRows.some((t) => t.is_unread) || (selectedRows.length === 0 && Boolean(ticket?.is_unread));
    const nextUnread = !markAsRead;

    for (const id of ids) {
      if (nextUnread) forceUnreadIdsRef.current.add(id);
      else forceUnreadIdsRef.current.delete(id);
    }

    const folderKey = ticket?.folder_id ?? folderId;

    // Optimistic local update — no list reload, no bulkBusy flash.
    setTickets((prev) =>
      prev.map((row) => (ids.includes(row.id) ? { ...row, is_unread: nextUnread } : row)),
    );
    if (ticket && ids.includes(ticket.id)) {
      setTicket({ ...ticket, is_unread: nextUnread });
    }
    setBulkSelectedIds(new Set());
    if (folderKey) {
      setUnreadByFolder((prev) => {
        const delta = nextUnread ? ids.length : -ids.length;
        const n = Math.max(0, (prev[folderKey] ?? 0) + delta);
        if (prev[folderKey] === n) return prev;
        return { ...prev, [folderKey]: n };
      });
    }

    bumpLocalEcho();
    try {
      await bulkUpdateTickets(ids, { is_unread: nextUnread });
      for (const id of ids) {
        await logTicketActivity(id, markAsRead ? 'mark_read' : 'mark_unread', {}, actorEmail);
      }
    } catch {
      // Revert optimistic patch on failure.
      setTickets((prev) =>
        prev.map((row) => (ids.includes(row.id) ? { ...row, is_unread: !nextUnread } : row)),
      );
      if (ticket && ids.includes(ticket.id)) {
        setTicket({ ...ticket, is_unread: !nextUnread });
      }
      void loadFolders();
    }
  }

  function resolveActionTicketIds(): string[] {
    if (bulkSelectedIds.size > 0) return [...bulkSelectedIds];
    return ticket?.id ? [ticket.id] : [];
  }

  async function handleRibbonDelete() {
    const ids = resolveActionTicketIds();
    if (ids.length === 0) return;
    const label = ids.length === 1 ? 'this ticket' : `${ids.length} tickets`;

    if (viewingTrash) {
      if (
        !window.confirm(
          `Permanently delete ${label}? This cannot be undone.`,
        )
      ) {
        return;
      }
      await runBulkAction(ids, async () => {
        await permanentlyDeleteTickets(ids, actorEmail);
      });
      if (ticket && ids.includes(ticket.id)) clearSelectedTicket();
      return;
    }

    if (!window.confirm(`Move ${label} to Trash?`)) return;
    await runBulkAction(ids, async () => {
      await bulkMoveTicketsToTrash(ids, actorEmail);
    });
    if (ticket && ids.includes(ticket.id)) clearSelectedTicket();
  }

  async function handleRibbonBlockSender() {
    const ids = resolveActionTicketIds();
    if (ids.length === 0) return;

    const fromList = tickets.filter((t) => ids.includes(t.id) && t.requester_email?.includes('@'));
    const targets =
      fromList.length > 0
        ? fromList
        : ticket?.requester_email?.includes('@') && ids.includes(ticket.id)
          ? [ticket]
          : [];

    const byEmail = new Map<string, string>();
    for (const t of targets) {
      const email = t.requester_email.trim().toLowerCase();
      if (!byEmail.has(email)) byEmail.set(email, t.id);
    }
    if (byEmail.size === 0) {
      window.alert('No sender email found for the selection.');
      return;
    }
    const label = byEmail.size === 1 ? [...byEmail.keys()][0] : `${byEmail.size} senders`;
    if (!window.confirm(`Block ${label}? Matching tickets will be marked as spam.`)) return;

    const gone = new Set(byEmail.values());
    const removedRows = tickets.filter((t) => gone.has(t.id));

    bumpLocalEcho();
    setTickets((prev) => prev.filter((t) => !gone.has(t.id)));
    setListTotal((prev) => Math.max(0, prev - gone.size));
    setBulkSelectedIds(new Set());
    setUnreadByFolder((prev) => {
      let next = prev;
      for (const row of removedRows) {
        if (!row.is_unread || !row.folder_id) continue;
        if (next === prev) next = { ...prev };
        next[row.folder_id] = Math.max(0, (next[row.folder_id] ?? 1) - 1);
      }
      return next;
    });
    if (ticket && gone.has(ticket.id)) clearSelectedTicket();

    try {
      for (const [email, ticketId] of byEmail) {
        await blockSender(email, actorEmail, ticketId);
      }
    } catch {
      void loadList({ quiet: true });
      void loadFolders();
    }
  }



  async function handleDropTicket(ticketId: string, targetFolderId: string) {
    const row =
      tickets.find((t) => t.id === ticketId) ??
      (ticket?.id === ticketId ? ticket : null);
    if (row?.folder_id === targetFolderId) return;

    const leavingCurrentView = Boolean(folderId && folderId !== targetFolderId);

    bumpLocalEcho();
    if (leavingCurrentView) {
      setTickets((prev) => prev.filter((t) => t.id !== ticketId));
      setListTotal((prev) => Math.max(0, prev - 1));
      setBulkSelectedIds((prev) => {
        if (!prev.has(ticketId)) return prev;
        const next = new Set(prev);
        next.delete(ticketId);
        return next;
      });
    } else {
      setTickets((prev) =>
        prev.map((t) => (t.id === ticketId ? { ...t, folder_id: targetFolderId } : t)),
      );
    }

    if (row?.is_unread) {
      setUnreadByFolder((prev) => {
        const next = { ...prev };
        if (row.folder_id) {
          next[row.folder_id] = Math.max(0, (next[row.folder_id] ?? 1) - 1);
        }
        next[targetFolderId] = (next[targetFolderId] ?? 0) + 1;
        return next;
      });
    }

    if (ticket?.id === ticketId) {
      setTicket({ ...ticket, folder_id: targetFolderId });
    }

    try {
      const updated = await updateTicket(ticketId, { folder_id: targetFolderId });
      if (ticket?.id === ticketId) setTicket(updated);
      await logTicketActivity(ticketId, 'move_folder', { folder_id: targetFolderId }, actorEmail);
    } catch {
      void loadList({ quiet: true });
      void loadFolders();
    }
  }



  async function handleTagChange(nextIds: string[]) {

    if (!ticket) return;

    bumpLocalEcho();
    await setTicketTags(ticket.id, nextIds);

    setTicketTagIds(nextIds);

    await logTicketActivity(ticket.id, 'tags_change', { tag_ids: nextIds }, actorEmail);

  }



  function escapeNoteText(text: string): string {
    return text
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;');
  }

  function noteHtmlFromText(text: string): string {
    return `<p>${escapeNoteText(text).replace(/\n/g, '<br>')}</p>`;
  }

  async function handleAddComment(text: string, files: File[]) {
    if (!ticket) return;
    setNoteBusy(true);
    try {
      const html = text.trim() ? noteHtmlFromText(text) : '<p></p>';
      const note = await addTicketNote(ticket.id, html, actorEmail);
      const savedAttachments: TicketAttachment[] = [];
      for (const file of files) {
        const uploaded = await uploadTicketAttachment(ticket.id, file);
        const row = await insertTicketMessageAttachment(note.id, {
          storage_path: uploaded.path,
          file_name: uploaded.file_name,
          content_type: uploaded.content_type,
          size_bytes: uploaded.size_bytes,
        });
        savedAttachments.push(row);
      }
      setMessages((prev) => [...prev, note]);
      if (savedAttachments.length > 0) {
        setAttachmentsByMessage((prev) => ({
          ...prev,
          [note.id]: [...(prev[note.id] ?? []), ...savedAttachments],
        }));
      }
    } finally {
      setNoteBusy(false);
    }
  }

  async function handleEditComment(noteId: string, text: string) {
    setNoteBusy(true);
    try {
      const updated = await updateTicketNote(noteId, noteHtmlFromText(text));
      setMessages((prev) => prev.map((m) => (m.id === noteId ? updated : m)));
    } finally {
      setNoteBusy(false);
    }
  }

  async function handleDeleteComment(noteId: string) {
    await deleteTicketMessage(noteId, actorEmail);
    setMessages((prev) => prev.filter((m) => m.id !== noteId));
  }

  function openEditNote(msg: TicketMessage) {
    setDetailsOpen(false);
    setActivityOpen(true);
    setEditNoteId(msg.id);
  }



  const assigneeOptions = useMemo(() => {

    const byEmail = new Map<string, { email: string; label: string }>();

    for (const agent of ticketAgents) {

      const email = agent.email.trim().toLowerCase();

      if (!email.includes('@')) continue;

      const name = agent.display_name.trim();

      byEmail.set(email, {

        email,

        label: name ? `${name} (${email})` : email,

      });

    }

    if (actorEmail) {

      const email = actorEmail.trim().toLowerCase();

      if (email.includes('@') && !byEmail.has(email)) {

        byEmail.set(email, { email, label: email });

      }

    }

    if (ticket?.assignee_email) {

      const email = ticket.assignee_email.trim().toLowerCase();

      if (email.includes('@') && !byEmail.has(email)) {

        byEmail.set(email, { email, label: email });

      }

    }

    return [...byEmail.values()].sort((a, b) => a.label.localeCompare(b.label));

  }, [ticketAgents, actorEmail, ticket?.assignee_email]);



  const currentStatusMeta = useMemo(

    () => statusDefs.find((s) => s.slug === ticket?.status),

    [statusDefs, ticket?.status],

  );



  function toggleBulkTicket(id: string, checked: boolean) {

    setBulkSelectedIds((prev) => {

      const next = new Set(prev);

      if (checked) next.add(id);

      else next.delete(id);

      return next;

    });

  }



  function toggleAllBulk(checked: boolean) {

    if (!checked) {

      setBulkSelectedIds(new Set());

      return;

    }

    setBulkSelectedIds(new Set(tickets.map((t) => t.id)));

  }



  async function runBulkAction(ids: string[], action: () => Promise<void>) {
    const idSet = new Set(ids);
    const removedRows = tickets.filter((t) => idSet.has(t.id));

    bumpLocalEcho();
    setTickets((prev) => prev.filter((t) => !idSet.has(t.id)));
    setListTotal((prev) => Math.max(0, prev - ids.length));
    setBulkSelectedIds(new Set());
    setUnreadByFolder((prev) => {
      let next = prev;
      for (const row of removedRows) {
        if (!row.is_unread || !row.folder_id) continue;
        if (next === prev) next = { ...prev };
        next[row.folder_id] = Math.max(0, (next[row.folder_id] ?? 1) - 1);
      }
      return next;
    });

    try {
      await action();
    } catch {
      void loadList({ quiet: true });
      void loadFolders();
    }
  }



  const ribbonShowsUnread =
    bulkSelectedIds.size > 0
      ? tickets.some((t) => bulkSelectedIds.has(t.id) && t.is_unread)
      : Boolean(ticket?.is_unread);
  const actionsDisabled = bulkSelectedIds.size === 0 && !ticket;

  if (allowed === null) {

    return (

      <div className="tickets-page">

        <PageHeader title="Tickets" />

        <p>Checking access…</p>

      </div>

    );

  }



  if (!allowed) {

    return (

      <div className="tickets-page">

        <PageHeader title="Tickets" />

        <p className="form-error">

          You do not have ticket access. Ask IT/Admin to add you as a ticket agent.

        </p>

      </div>

    );

  }



  return (

    <div className="tickets-page">

      <div className="tickets-page__sticky">

        <PageHeader

          title="Tickets"

          description="Incoming email is recorded as tickets as it arrives. New tickets receive an automatic acknowledgement."

        />



        <TicketRibbon

          disabled={!ticket}

          actionsDisabled={actionsDisabled}

          isUnread={ribbonShowsUnread}

          deleteLabel={viewingTrash ? 'Delete permanently' : 'Delete'}

          onReply={() => openCompose('reply')}

          onReplyAll={() => openCompose('reply_all')}

          onForward={() => openCompose('forward')}

          onNewEmail={() => openCompose('new')}

          onBlockSender={() => void handleRibbonBlockSender()}

          onToggleRead={() => void handleToggleRead()}

          onDelete={() => void handleRibbonDelete()}

          onCreateProject={() => setProjectModalOpen(true)}

          onCreateTask={() => setTaskModalOpen(true)}

          onNewAccount={() => setAccountModalOpen(true)}

          onMerge={() => setMergeOpen(true)}

          addContactDisabled={!ticket?.client_id}

          showCrmActions={!isItPortal}

          onAddContact={() => {
            if (!ticket?.client_id) return;
            setContactModalClientId(ticket.client_id);
            setContactModalEmail(ticket.requester_email || '');
            setContactModalOpen(true);
          }}

        />

      </div>

      <div className="tickets-page__body">

      <div
        className={[
          'tickets-layout',
          selectedId ? 'tickets-layout--detail-open' : 'tickets-layout--list-only',
        ].join(' ')}
      >

        <div className="tickets-list-pane">
          <div
            className={[
              'tickets-folders-shell',
              'tickets-folders-shell--dock',
              foldersOpen ? ' is-open' : '',
            ].join(' ')}
            onMouseLeave={(e) => {
              const el = document.activeElement;
              if (el instanceof HTMLElement && e.currentTarget.contains(el)) {
                el.blur();
              }
            }}
          >
            <button
              type="button"
              className="tickets-folders-shell__backdrop"
              aria-label="Close folders"
              onClick={() => setFoldersOpen(false)}
            />
            <TicketFolderSidebar
              folders={folders}
              unreadByFolder={unreadByFolder}
              activeFolderId={folderId}
              drawerOpen={foldersOpen}
              allowCreateFolders={!isItPortal}
              onSelect={selectFolder}
              onCreateFolder={handleCreateFolder}
              onDeleteFolder={handleDeleteFolder}
              onDropTicket={(ticketId, targetFolderId) => void handleDropTicket(ticketId, targetFolderId)}
            />
          </div>

          <section className="tickets-mid">

          <div className="tickets-mid__toolbar">

            <button
              type="button"
              className="tickets-mid__folders-btn"
              onClick={() => setFoldersOpen(true)}
            >
              <span className="tickets-mid__folders-btn-label">Folder</span>
              <span className="tickets-mid__folders-btn-value">{activeFolderName}</span>
            </button>

            <input

              className="field-input"

              placeholder="Search tickets"

              value={search}

              onChange={(e) => setSearch(e.target.value)}

            />

            <select

              className="field-input field-input--compact"

              value={statusFilter}

              onChange={(e) => setStatusFilter(e.target.value as TicketStatus | 'all')}

            >

              <option value="all">All statuses</option>

              {statusDefs.map((s) => (

                <option key={s.slug} value={s.slug}>

                  {s.name}

                </option>

              ))}

            </select>

          </div>

          {listError && <p className="form-error">{listError}</p>}

          <TicketList

            tickets={tickets}

            selectedId={selectedId}

            bulkSelectedIds={bulkSelectedIds}

            clientNameById={clientNameById}

            contactNameById={contactNameById}

            loading={listLoading}

            statusLabels={statusLabelMap}

            onSelect={selectTicket}

            onToggleBulk={toggleBulkTicket}

            onToggleAllBulk={toggleAllBulk}

          />

          {listTotal > PAGE_SIZE && (

            <footer className="tickets-mid__pager">

              <button

                type="button"

                className="tickets-mid__pager-btn"

                disabled={listPage <= 1 || listLoading}

                onClick={() => setListPage((p) => Math.max(1, p - 1))}

              >

                Previous

              </button>

              <span className="tickets-mid__pager-meta">

                Page {listPage} of {listPageCount}

                <span className="tickets-mid__pager-total"> · {listTotal} tickets</span>

              </span>

              <button

                type="button"

                className="tickets-mid__pager-btn"

                disabled={listPage >= listPageCount || listLoading}

                onClick={() => setListPage((p) => Math.min(listPageCount, p + 1))}

              >

                Next

              </button>

            </footer>

          )}

          </section>
        </div>

        <section className="tickets-detail">

          {!selectedId && (

            <p className="tickets-detail__empty">Select a ticket to view the thread.</p>

          )}

          {selectedId && detailLoading && (!ticket || ticket.id !== selectedId) && (

            <p className="tickets-detail__loading">Loading conversation…</p>

          )}

          {detailError && <p className="form-error">{detailError}</p>}

          {ticket && ticket.id === selectedId && (

            <div className="tickets-detail__inner">

              <header className="tickets-detail__header tickets-detail__header--slim">
                <div className="tickets-detail__nav-left">
                  <button
                    type="button"
                    className="tickets-detail__back"
                    onClick={clearSelectedTicket}
                  >
                    ← Back to list
                  </button>

                  <button
                    type="button"
                    className="tickets-mid__folders-btn tickets-detail__folders-btn"
                    onClick={() => setFoldersOpen(true)}
                  >
                    <span className="tickets-mid__folders-btn-label">Folder</span>
                    <span className="tickets-mid__folders-btn-value">{activeFolderName}</span>
                  </button>
                </div>

                <div className="tickets-detail__tablet-extras">
                  <div className="tickets-detail__compact-tabs" role="tablist" aria-label="Ticket detail tabs">
                    <button
                      type="button"
                      role="tab"
                      aria-selected={detailTab === 'messages'}
                      className={`tickets-detail__tab tickets-detail__tab--compact${detailTab === 'messages' ? ' is-active' : ''}`}
                      onClick={() => setDetailTab('messages')}
                    >
                      Messages
                    </button>
                    <button
                      type="button"
                      role="tab"
                      aria-selected={detailTab === 'attachments'}
                      className={`tickets-detail__tab tickets-detail__tab--compact${detailTab === 'attachments' ? ' is-active' : ''}`}
                      onClick={() => setDetailTab('attachments')}
                    >
                      Attachments
                    </button>
                  </div>

                  <select
                    className="field-input field-input--compact tickets-detail__status-select tickets-detail__status-select--compact"
                    value={ticket.status}
                    aria-label="Ticket status"
                    onChange={(e) =>
                      void patchTicket({ status: e.target.value as TicketStatus }, 'status_change')
                    }
                  >
                    {statusDefs.map((s) => (
                      <option key={s.slug} value={s.slug}>
                        {s.name}
                      </option>
                    ))}
                  </select>
                </div>

                <div className="tickets-detail__nav-actions">
                  <button
                    ref={activityBtnRef}
                    type="button"
                    className={`tickets-detail__context-btn tickets-detail__context-btn--inline${activityOpen ? ' is-active' : ''}`}
                    onClick={() => {
                      setDetailsOpen(false);
                      setActivityOpen((v) => !v);
                    }}
                  >
                    Internal notes
                  </button>
                  <button
                    ref={detailsBtnRef}
                    type="button"
                    className={`tickets-detail__context-btn tickets-detail__context-btn--inline${detailsOpen ? ' is-active' : ''}`}
                    onClick={() => {
                      setActivityOpen(false);
                      setDetailsOpen((v) => !v);
                    }}
                  >
                    Details
                  </button>
                </div>
              </header>

              <div className="tickets-detail__account-strip">
                <div className="tickets-detail__account-strip-main tickets-detail__account-strip-main--desktop">
                  <span className="tickets-detail__account-strip-label">Account</span>
                  <span className="tickets-detail__account-strip-value">
                    {linkedClient
                      ? `${linkedClient.company_name || linkedClient.code || 'Account'}${linkedClient.code ? ` (${linkedClient.code})` : ''}`
                      : 'No account linked'}
                  </span>
                </div>
                <div className="tickets-detail__account-strip-main tickets-detail__account-strip-main--desktop">
                  <span className="tickets-detail__account-strip-label">Contact</span>
                  <span className="tickets-detail__account-strip-value">
                    {(() => {
                      const c = crmData.contacts.find((x) =>
                        idsMatch(x.id, ticket.contact_id != null ? String(ticket.contact_id) : ''),
                      );
                      if (c) {
                        const name = [c.first_name, c.last_name].filter(Boolean).join(' ').trim();
                        return name || c.email || 'Contact';
                      }
                      return ticket.requester_name || ticket.requester_email || 'No contact linked';
                    })()}
                    {' · '}
                    <button
                      type="button"
                      className="tickets-detail__account-strip-edit-link"
                      onClick={() => setLinkAccountModalOpen(true)}
                    >
                      Edit
                    </button>
                  </span>
                </div>
                <div className="tickets-detail__account-strip-actions tickets-detail__account-strip-actions--desktop">
                  <div className="tickets-detail__account-strip-tags">
                    <TicketTagChips
                      tags={tagDefs}
                      selectedIds={ticketTagIds}
                      hideLabel
                      onChange={(next) => void handleTagChange(next)}
                      onCreateTag={async (name) => {
                        const mailboxId =
                          ticket?.mailbox_id || (await resolvePortalMailboxId());
                        if (!mailboxId) throw new Error('No mailbox configured for tags.');
                        const created = await upsertTicketTagDefinition({
                          name,
                          mailbox_id: mailboxId,
                        });
                        await loadTags();
                        return created;
                      }}
                    />
                  </div>
                  <select
                    className="field-input field-input--compact tickets-detail__status-select"
                    value={ticket.status}
                    aria-label="Ticket status"
                    onChange={(e) =>
                      void patchTicket({ status: e.target.value as TicketStatus }, 'status_change')
                    }
                  >
                    {statusDefs.map((s) => (
                      <option key={s.slug} value={s.slug}>
                        {s.name}
                      </option>
                    ))}
                  </select>
                  <button
                    ref={activityDesktopRef}
                    type="button"
                    className={`tickets-detail__context-btn tickets-detail__context-btn--inline${activityOpen ? ' is-active' : ''}`}
                    onClick={() => {
                      setDetailsOpen(false);
                      setActivityOpen((v) => !v);
                    }}
                  >
                    Internal notes
                  </button>
                  <button
                    ref={detailsDesktopRef}
                    type="button"
                    className={`tickets-detail__context-btn tickets-detail__context-btn--inline${detailsOpen ? ' is-active' : ''}`}
                    onClick={() => {
                      setActivityOpen(false);
                      setDetailsOpen((v) => !v);
                    }}
                  >
                    Details
                  </button>
                </div>
              </div>

              {attachMsg && (

                <p

                  className={

                    /failed|error|no attachments|missing graph/i.test(attachMsg)

                      ? 'form-error tickets-detail__attach-msg'

                      : 'tickets-detail__attach-msg'

                  }

                >

                  {attachMsg}

                </p>

              )}

              {attachRefreshing && (

                <p className="tickets-detail__attach-msg">Loading images and attachments from mailbox…</p>

              )}

              <div className="tickets-detail__body">

                <div className="tickets-detail__thread">
                  <div className="tickets-detail__tabs-bar">
                    <div className="tickets-detail__tabs" role="tablist" aria-label="Ticket detail tabs">
                      <button
                        type="button"
                        role="tab"
                        aria-selected={detailTab === 'messages'}
                        className={`tickets-detail__tab${detailTab === 'messages' ? ' is-active' : ''}`}
                        onClick={() => setDetailTab('messages')}
                      >
                        Messages
                      </button>
                      <button
                        type="button"
                        role="tab"
                        aria-selected={detailTab === 'attachments'}
                        className={`tickets-detail__tab${detailTab === 'attachments' ? ' is-active' : ''}`}
                        onClick={() => setDetailTab('attachments')}
                      >
                        Attachments
                      </button>
                    </div>
                    <select
                      className="field-input field-input--compact tickets-detail__status-select tickets-detail__status-select--mobile"
                      value={ticket.status}
                      aria-label="Ticket status"
                      onChange={(e) =>
                        void patchTicket({ status: e.target.value as TicketStatus }, 'status_change')
                      }
                    >
                      {statusDefs.map((s) => (
                        <option key={s.slug} value={s.slug}>
                          {s.name}
                        </option>
                      ))}
                    </select>
                  </div>

                  {detailTab === 'messages' ? (
                    <TicketMessageTimeline
                      messages={customerMessages}
                      attachmentsByMessage={attachmentsByMessage}
                      loading={detailLoading || attachRefreshing}
                      onEditNote={openEditNote}
                      onDeleteMessage={async (messageId) => {
                        await deleteTicketMessage(messageId, actorEmail);
                        setMessages((prev) => prev.filter((m) => m.id !== messageId));
                        setAttachmentsByMessage((prev) => {
                          const next = { ...prev };
                          delete next[messageId];
                          return next;
                        });
                      }}
                    />
                  ) : (
                    <TicketAttachmentsPanel
                      messages={messages}
                      attachmentsByMessage={attachmentsByMessage}
                      loading={detailLoading || attachRefreshing}
                    />
                  )}
                </div>

              </div>

            </div>

          )}

        </section>

      </div>

      </div>



      {ticket && (
        <TicketBubblePanel
          open={activityOpen}
          title="Internal notes"
          onClose={() => setActivityOpen(false)}
          className="ticket-bubble-panel--activity"
          anchorRef={narrowDetail ? activityBtnRef : activityDesktopRef}
        >
          <TicketActivityBubble
            notes={ticketNotes}
            busy={noteBusy}
            startEditId={editNoteId}
            onEditStarted={() => setEditNoteId(null)}
            onAdd={handleAddComment}
            onEdit={handleEditComment}
            onDelete={handleDeleteComment}
          />
        </TicketBubblePanel>
      )}

      {ticket && (
        <TicketBubblePanel
          open={detailsOpen}
          title="Details"
          onClose={() => setDetailsOpen(false)}
          className="ticket-bubble-panel--details"
          anchorRef={narrowDetail ? detailsBtnRef : detailsDesktopRef}
        >
          <div className="tickets-detail__modal">
            <div className="tickets-detail__meta-card">
              <p className="tickets-detail__number">{ticket.ticket_number}</p>
              <div className="tickets-detail__title-row">
                <h3 className="tickets-detail__subject tickets-detail__subject--side">{ticket.subject}</h3>
                {currentStatusMeta ? (
                  <span
                    className="tickets-detail__status-badge"
                    style={{
                      background: `${currentStatusMeta.color}18`,
                      color: currentStatusMeta.color,
                      borderColor: `${currentStatusMeta.color}40`,
                    }}
                  >
                    {currentStatusMeta.name}
                  </span>
                ) : null}
              </div>
              <p className="tickets-detail__requester">
                {ticket.requester_name || 'Customer'} &lt;{ticket.requester_email || '—'}&gt;
              </p>
              {followUpWorkdays > 0 && ticketNeedsFollowUp(ticket, followUpWorkdays) ? (
                <div className="tickets-detail__follow-up-banner">
                  <p>Follow-up due — remind the assignee to reply (no email is sent automatically).</p>
                  <button
                    type="button"
                    className="btn-secondary btn-sm"
                    disabled={followUpBusy}
                    onClick={() => {
                      setFollowUpBusy(true);
                      void sendTicketFollowUpReminder(ticket, actorEmail, { notifyOnly: true })
                        .then(async () => {
                          const refreshed = await fetchTicket(ticket.id);
                          if (refreshed) setTicket(refreshed);
                        })
                        .catch(() => undefined)
                        .finally(() => setFollowUpBusy(false));
                    }}
                  >
                    {followUpBusy ? 'Saving…' : 'Mark reminded'}
                  </button>
                </div>
              ) : null}
              <div className="tickets-detail__props tickets-detail__props--stack">
                <label>
                  Status
                  <select
                    className="field-input field-input--compact"
                    value={ticket.status}
                    onChange={(e) =>
                      void patchTicket({ status: e.target.value as TicketStatus }, 'status_change')
                    }
                  >
                    {statusDefs.map((s) => (
                      <option key={s.slug} value={s.slug}>
                        {s.name}
                      </option>
                    ))}
                  </select>
                </label>
                <label>
                  Assignee
                  <select
                    className="field-input field-input--compact"
                    value={ticket.assignee_email ?? ''}
                    onChange={(e) =>
                      void patchTicket({ assignee_email: e.target.value }, 'assignee_change')
                    }
                  >
                    <option value="">Unassigned</option>
                    {assigneeOptions.map((opt) => (
                      <option key={opt.email} value={opt.email}>
                        {opt.label}
                      </option>
                    ))}
                  </select>
                </label>
                <label>
                  Priority
                  <select
                    className="field-input field-input--compact"
                    value={ticket.priority}
                    onChange={(e) =>
                      void patchTicket(
                        { priority: e.target.value as TicketPriority },
                        'priority_change',
                      )
                    }
                  >
                    <option value="low">Low</option>
                    <option value="normal">Normal</option>
                    <option value="high">High</option>
                    <option value="urgent">Urgent</option>
                  </select>
                </label>
              </div>
              <div className="tickets-detail__tags">
                <TicketTagChips
                  tags={tagDefs}
                  selectedIds={ticketTagIds}
                  onChange={(next) => void handleTagChange(next)}
                  onCreateTag={async (name) => {
                    const mailboxId =
                      ticket?.mailbox_id || (await resolvePortalMailboxId());
                    if (!mailboxId) throw new Error('No mailbox configured for tags.');
                    const created = await upsertTicketTagDefinition({
                      name,
                      mailbox_id: mailboxId,
                    });
                    setTagDefs((prev) =>
                      [...prev, created].sort(
                        (a, b) =>
                          (a.sort_order ?? 0) - (b.sort_order ?? 0) ||
                          a.name.localeCompare(b.name),
                      ),
                    );
                    return created;
                  }}
                />
              </div>
            </div>
            <TicketAccountPanel
              client={linkedClient ?? null}
              contacts={crmData.contacts}
              projects={projectsForClient}
              tasks={tasksForClient}
              selectedContactId={ticket.contact_id != null ? String(ticket.contact_id) : ''}
              requesterEmail={ticket.requester_email}
              onEditLink={() => {
                setDetailsOpen(false);
                setLinkAccountModalOpen(true);
              }}
            />
          </div>
        </TicketBubblePanel>
      )}

      <TicketMergeModal
        open={mergeOpen}
        target={ticket}
        busy={mergeBusy}
        onClose={() => setMergeOpen(false)}
        onMerge={async (sourceId) => {
          if (!ticket) return;
          setMergeBusy(true);
          try {
            const merged = await mergeTickets(ticket.id, sourceId, actorEmail);
            setTicket(merged);
            await loadDetail(merged.id);
            void loadList();
            void loadFolders();
          } finally {
            setMergeBusy(false);
          }
        }}
      />

      {(composeOpen || ticket) && (

        <TicketComposeModal

          open={composeOpen}

          mode={composeMode}

          subject={composeMode === 'new' ? '' : (ticket?.subject ?? '')}

          defaultTo={composeMode === 'new' ? '' : (ticket?.requester_email ?? '')}

          messages={composeMode === 'new' ? [] : customerMessages}

          attachmentsByMessage={composeMode === 'new' ? {} : attachmentsByMessage}

          mailboxEmail={
            (ticket?.mailbox_id
              ? ticketMailboxes.find((m) => m.id === ticket.mailbox_id)?.address
              : ticketMailboxes[0]?.address) || actorEmail
          }
          mailboxId={ticket?.mailbox_id ?? ticketMailboxes[0]?.id ?? null}
          agentEmail={actorEmail}

          accountContacts={composeContactOptions}

          busy={sendBusy}

          onClose={() => setComposeOpen(false)}

          onUpload={(file) => uploadTicketAttachment(ticket?.id || 'draft', file)}

          onSend={async (payload) => {

            setComposeOpen(false);

            setSendBusy(true);

            try {

              const result = await sendTicketReply({

                ticketId: payload.mode === 'new' ? '' : ticket?.id ?? '',

                bodyHtml: payload.bodyHtml,

                toEmails: payload.toEmails,

                ccEmails: payload.ccEmails,

                attachments: payload.attachments,

                mode: payload.mode,

                subject: payload.subject,

              });

              void loadList({ quiet: true });

              if (result.ticket_id) {

                selectTicket(result.ticket_id);

              } else if (ticket) {

                setTicket((prev) => {
                  if (!prev) return prev;
                  const next = {
                    ...prev,
                    assignee_email: actorEmail,
                    is_unread: false,
                  };
                  if (prev.status === 'open') next.status = 'processing';
                  return next;
                });
                setTickets((prev) =>
                  prev.map((row) => {
                    if (row.id !== ticket.id) return row;
                    const next = { ...row, assignee_email: actorEmail, is_unread: false };
                    if (row.status === 'open') next.status = 'processing';
                    return next;
                  }),
                );
                void loadDetail(ticket.id, { quiet: true, markRead: false });

              }

            } catch (e) {

              setListError(e instanceof Error ? e.message : 'Send failed.');

            } finally {

              setSendBusy(false);

            }

          }}

        />

      )}



      <AccountFormModal

        open={accountModalOpen}

        onClose={() => setAccountModalOpen(false)}

        onSaved={async (result) => {

          await refreshCrm();

          if (ticket) {

            const match = crmData.clients.find((c) => c.code === result.code);

            if (match) await handleLinkAccountSave(String(match.id), '');

          }

        }}

      />

      <TicketLinkAccountModal
        open={linkAccountModalOpen}
        clients={crmData.clients}
        contacts={crmData.contacts}
        clientId={ticket?.client_id != null ? String(ticket.client_id) : ''}
        contactId={ticket?.contact_id != null ? String(ticket.contact_id) : ''}
        requesterEmail={ticket?.requester_email}
        onClose={() => setLinkAccountModalOpen(false)}
        onSave={handleLinkAccountSave}
      />



      <ContactFormModal

        open={contactModalOpen}

        onClose={() => {

          setContactModalOpen(false);

          void refreshCrm().then(() => {

            if (ticket) void loadDetail(ticket.id);

          });

        }}

        defaultClientId={contactModalClientId}

        lockClient

        contact={

          contactModalEmail

            ? {

                id: '',

                first_name: ticket?.requester_name?.split(' ')[0] ?? '',

                last_name: ticket?.requester_name?.split(' ').slice(1).join(' ') ?? '',

                email: contactModalEmail,

                client_id: contactModalClientId,

                job_title: '',

                phone: '',

              }

            : null

        }

      />



      <ProjectFormModal

        open={projectModalOpen}

        onClose={() => setProjectModalOpen(false)}

        defaultClientId={ticket?.client_id ?? ''}

        lockClient={Boolean(ticket?.client_id)}

        defaultTitle={ticket?.subject ?? ''}

      />



      <TaskFormModal

        open={taskModalOpen}

        onClose={() => setTaskModalOpen(false)}

        defaultClientId={ticket?.client_id ?? ''}

        lockClient={Boolean(ticket?.client_id)}

        defaultTitle={ticket?.subject ?? ''}

      />

    </div>

  );

}


